import React from 'react';
import i18next from 'i18next';
import { Card, CardBody, CardHeader, Row } from 'reactstrap';

import { currency } from 'globals/currency';
import { countArrayItemsByField, sumArrayItemsByField } from 'utils/array';
import { fixDecimal } from 'utils/general';
import { getDeliveryMethodLabel, getPaymentMethodLabel } from 'utils/orders';
import PieChartWithAmount from './PieChartWithAmount';
import StatisticsColumn from './StatisticsColumn';
import type { IOrder } from 'types';
import { EDeliveryType } from 'enums';

type OrdersStatisticsSummaryProps = {
  ordersStats: IOrder[];
  statisticsData: {
    total_orders_count: number;
    total_orders_sum: number;
    total_payout_amount: number;
  };
};

const OrdersStatisticsSummary: React.FC<OrdersStatisticsSummaryProps> = (
  props
) => {
  const { ordersStats, statisticsData } = props;

  const ordersByDeliveryCount = countArrayItemsByField(
    ordersStats,
    'delivery_type'
  );
  const ordersByPaymentAmount = countArrayItemsByField(
    ordersStats,
    'payment_type'
  );
  const ordersByPaymentSum = sumArrayItemsByField(
    ordersStats,
    'payment_type',
    'price'
  );
  const ordersBySource = countArrayItemsByField(ordersStats, 'source');

  const ordersByDeliveryArray = Object.entries(ordersByDeliveryCount).map(
    ([delivery_type, ordersAmount]) => ({
      ordersAmount,
      delivery_type: getDeliveryMethodLabel(delivery_type as EDeliveryType),
    })
  );
  const ordersByPaymentArray = Object.keys(ordersByPaymentAmount).map(
    (paymentType) => ({
      paymentTypeLabel: i18next.t(
        `payment-types.${getPaymentMethodLabel(paymentType, false)}`
      ),
      ordersAmount: ordersByPaymentAmount[paymentType],
      ordersSum: fixDecimal(ordersByPaymentSum[paymentType]),
    })
  );
  const ordersBySourceArray = Object.entries(ordersBySource).map(
    ([source, ordersAmount]) => ({
      ordersAmount,
      source: i18next.t(`order.sources.${source}`),
    })
  );

  return (
    <>
      <div className="mb-3">
        <div>
          <strong className="text-danger">{i18next.t('orders-statistics-summary.warning')}:</strong>{' '}
          {i18next.t('orders-statistics-summary.disclaimer-1')}
        </div>
        <div>
          {i18next.t('orders-statistics-summary.disclaimer-2')}
        </div>
        <div>
          {i18next.t('orders-statistics-summary.disclaimer-3')}
        </div>
      </div>
      <Card>
        <CardHeader className="d-flex flex-row align-items-center">
          <span className="mr-2">{i18next.t('orders-statistics-summary.period-summary')}</span>
        </CardHeader>
        <CardBody>
          <Row className="m-b-md align-items-start">
            <StatisticsColumn
              title={i18next.t('orders-statistics-summary.orders-count')}
              value={statisticsData.total_orders_count}
            />
            <StatisticsColumn
              title={i18next.t('orders-statistics-summary.orders-turnover')}
              value={`${fixDecimal(
                statisticsData.total_orders_sum
              )} ${currency}`}
            />
            <StatisticsColumn
              title={i18next.t('orders-statistics-summary.payout-amount')}
              value={`${fixDecimal(
                statisticsData.total_payout_amount
              )} ${currency}`}
            >
              <div>{i18next.t('orders-statistics-summary.payout-amount-hint')}</div>
            </StatisticsColumn>
          </Row>
          <Row className="m-b-md align-items-start bt-1">
            <StatisticsColumn
              className="mt-3"
              title={i18next.t('orders-statistics-summary.orders-by-source')}
            >
              <PieChartWithAmount
                data={ordersBySourceArray}
                dataField="ordersAmount"
                labelField="source"
                showSelect={false}
                showText
              />
            </StatisticsColumn>
            <StatisticsColumn
              className="mt-3"
              title={i18next.t('orders-statistics-summary.orders-by-delivery')}
            >
              <PieChartWithAmount
                data={ordersByDeliveryArray}
                dataField="ordersAmount"
                labelField="delivery_type"
                showSelect={false}
                showText
              />
            </StatisticsColumn>
            <StatisticsColumn
              className="mt-3"
              title={i18next.t('orders-statistics-summary.orders-by-payment')}
            >
              <PieChartWithAmount
                data={ordersByPaymentArray}
                dataField="ordersAmount"
                labelField="paymentTypeLabel"
                textFormatter={(item) =>
                  `${item.paymentTypeLabel}: ${item.ordersAmount} ${i18next.t('orders-statistics-summary.pcs')} (${item.ordersSum} ${currency})`
                }
                showSelect={false}
                showText
              />
            </StatisticsColumn>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default React.memo(OrdersStatisticsSummary);
